import { useState, useCallback, useEffect, useRef } from 'react';
import { TreeItem } from './TreeItem';
import { TreeItemWrapper } from './TreeItemWrapper';
import { TreeSkeletons } from './TreeSkeletons';
import { TreeError } from './TreeError';
import { TreeEmpty } from './TreeEmpty';
import { LoadMoreButton } from './LoadMoreButton';
import type { FileNode } from './types';

export interface LoadChildrenResult {
  items: FileNode[];
  nextCursor?: string | null;
  hasNextPage?: boolean;
}

export type LoadChildrenFn = (
  folder: FileNode,
  options?: { after?: string }
) => Promise<LoadChildrenResult>;

interface FileTreeProps {
  files: FileNode[];
  selectedFile?: FileNode | null;
  expandedFolders: Set<string>;
  onFileSelect?: (file: FileNode) => void;
  /**
   * Double-click on a file — "open this for good", as opposed to the single-click
   * preview `onFileSelect` represents. Folders are unaffected: their double-click
   * is two toggles, which is what a user expects from a disclosure triangle.
   */
  onFileOpen?: (file: FileNode) => void;
  onToggleFolder?: (folderId: string) => void;
  /** Lazy-load children when a folder is expanded. If not provided, uses file.children. */
  loadChildren?: LoadChildrenFn;
  onRefreshMetadataType?: (metadataType: string) => void;
  onAddFile?: (metadataType: string) => void;
  refreshingType?: string | null;
  isSyncInProgress?: boolean;
  /** Called when the user clicks the copy-name button on a file node. */
  onCopyName?: (file: FileNode) => void;
}

export function FileTree({
  files,
  selectedFile,
  expandedFolders,
  onFileSelect,
  onFileOpen,
  onToggleFolder,
  loadChildren,
  onRefreshMetadataType,
  onAddFile,
  refreshingType,
  isSyncInProgress,
  onCopyName,
}: FileTreeProps) {
  const preloadCacheRef = useRef<Record<string, LoadChildrenResult>>({});

  return (
    <div className="space-y-1">
      {files.map((file) => (
        <FileTreeNode
          key={file.id}
          file={file}
          depth={0}
          preloadCacheRef={preloadCacheRef}
          selectedFile={selectedFile}
          expandedFolders={expandedFolders}
          onFileSelect={onFileSelect}
          onFileOpen={onFileOpen}
          onToggleFolder={onToggleFolder}
          loadChildren={loadChildren}
          onRefreshMetadataType={onRefreshMetadataType}
          onAddFile={onAddFile}
          refreshingType={refreshingType}
          isSyncInProgress={isSyncInProgress}
          onCopyName={onCopyName}
        />
      ))}
    </div>
  );
}

interface FileTreeNodeProps {
  file: FileNode;
  depth: number;
  preloadCacheRef: React.MutableRefObject<Record<string, LoadChildrenResult>>;
  selectedFile?: FileNode | null;
  expandedFolders: Set<string>;
  onFileSelect?: (file: FileNode) => void;
  onFileOpen?: (file: FileNode) => void;
  onToggleFolder?: (folderId: string) => void;
  loadChildren?: LoadChildrenFn;
  onRefreshMetadataType?: (metadataType: string) => void;
  onAddFile?: (metadataType: string) => void;
  refreshingType?: string | null;
  isSyncInProgress?: boolean;
  onCopyName?: (file: FileNode) => void;
}

function FileTreeNode({
  file,
  depth,
  preloadCacheRef,
  selectedFile,
  expandedFolders,
  onFileSelect,
  onFileOpen,
  onToggleFolder,
  loadChildren,
  onRefreshMetadataType,
  onAddFile,
  refreshingType,
  isSyncInProgress,
  onCopyName,
}: FileTreeNodeProps) {
  const isExpanded = expandedFolders.has(file.id);
  const isFolder = file.type === 'folder';
  const metadataType = file.metadataType;
  const folderCount = file.count;
  const isRefreshing = isFolder && metadataType === refreshingType;

  const [loadedItems, setLoadedItems] = useState<FileNode[]>([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preloading, setPreloading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageInfo, setPageInfo] = useState<{
    nextCursor?: string | null;
    hasNextPage?: boolean;
  }>({});

  const hasStaticChildren = isFolder && file.children && file.children.length > 0;
  const useLazyLoad = isFolder && isExpanded && !!loadChildren && !hasStaticChildren;

  const load = useCallback(
    async (after?: string) => {
      if (!loadChildren || !isFolder) return;
      const isInitial = after == null;
      const cached = isInitial ? preloadCacheRef.current[file.id] : undefined;
      if (cached && isInitial) {
        setLoadedItems(cached.items ?? []);
        setHasLoadedInitial(true);
        setError(null);
        setPageInfo({
          nextCursor: cached.nextCursor ?? null,
          hasNextPage: cached.hasNextPage ?? false,
        });
        delete preloadCacheRef.current[file.id];
        return;
      }
      if (isInitial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const result = await loadChildren(file, { after: after ?? undefined });
        const items = result.items ?? [];
        if (isInitial) {
          setLoadedItems(items);
          setHasLoadedInitial(true);
        } else {
          setLoadedItems((prev) => [...prev, ...items]);
        }
        setPageInfo({
          nextCursor: result.nextCursor ?? null,
          hasNextPage: result.hasNextPage ?? false,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error loading files');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [loadChildren, file, isFolder, preloadCacheRef]
  );

  useEffect(() => {
    if (useLazyLoad) {
      load();
    }
  }, [useLazyLoad, file.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isExpanded) {
      setLoadedItems([]);
      setHasLoadedInitial(false);
      setPageInfo({});
    }
  }, [isExpanded]);

  const handleLoadMore = useCallback(() => {
    if (pageInfo.hasNextPage && pageInfo.nextCursor) {
      load(pageInfo.nextCursor);
    }
  }, [pageInfo.hasNextPage, pageInfo.nextCursor, load]);

  const handleClick = useCallback(() => {
    if (isFolder) {
      if (isExpanded) {
        onToggleFolder?.(file.id);
      } else if (loadChildren && !hasStaticChildren) {
        setPreloading(true);
        loadChildren(file)
          .then((result) => {
            preloadCacheRef.current[file.id] = result;
            onToggleFolder?.(file.id);
          })
          .catch((err) => {
            setError(err instanceof Error ? err.message : 'Error loading files');
          })
          .finally(() => {
            setPreloading(false);
          });
      } else {
        onToggleFolder?.(file.id);
      }
    } else {
      onFileSelect?.(file);
    }
  }, [
    isFolder,
    isExpanded,
    loadChildren,
    hasStaticChildren,
    file,
    onToggleFolder,
    onFileSelect,
    preloadCacheRef,
  ]);

  // Files only. A folder's double-click is two toggles — the disclosure
  // behaviour a tree is expected to have — so it is left alone.
  const handleDoubleClick = useCallback(() => {
    if (isFolder) return;
    onFileOpen?.(file);
  }, [isFolder, file, onFileOpen]);

  const handleRefresh =
    isFolder && metadataType && onRefreshMetadataType
      ? () => onRefreshMetadataType(metadataType)
      : undefined;
  const handleAdd =
    isFolder && metadataType && onAddFile ? () => onAddFile(metadataType) : undefined;
  const handleCopyName =
    !isFolder && onCopyName ? () => onCopyName(file) : undefined;

  const childrenToRender =
    hasStaticChildren && isFolder && isExpanded ? file.children! : loadedItems;
  const isLoadingChildren = useLazyLoad && loading && !hasLoadedInitial;
  const isLoadingMoreChildren = useLazyLoad && loadingMore;

  return (
    <div>
      <TreeItem
        file={file}
        depth={depth}
        isSelected={selectedFile?.id === file.id}
        isExpanded={isExpanded}
        isLoading={isLoadingChildren || preloading}
        isRefreshing={isRefreshing}
        isSyncInProgress={isSyncInProgress}
        itemCount={folderCount}
        onClick={handleClick}
        onDoubleClick={onFileOpen && !isFolder ? handleDoubleClick : undefined}
        onRefresh={handleRefresh}
        onAddFile={handleAdd}
        onCopyName={handleCopyName}
      />

      {isFolder && isExpanded && (
        <div>
          {isLoadingChildren ? (
            <TreeSkeletons depth={depth + 1} count={5} />
          ) : error ? (
            <TreeError depth={depth + 1} message={error} />
          ) : childrenToRender && childrenToRender.length > 0 ? (
            <>
              {childrenToRender.map((child, index) => {
                const isLast = index === childrenToRender.length - 1;
                return (
                  <TreeItemWrapper key={child.id} depth={depth + 1} isLastChild={isLast}>
                    <FileTreeNode
                      file={child}
                      depth={depth + 1}
                      selectedFile={selectedFile}
                      expandedFolders={expandedFolders}
                      onFileSelect={onFileSelect}
                      onFileOpen={onFileOpen}
                      onToggleFolder={onToggleFolder}
                      preloadCacheRef={preloadCacheRef}
                      loadChildren={loadChildren}
                      onRefreshMetadataType={onRefreshMetadataType}
                      onAddFile={onAddFile}
                      refreshingType={refreshingType}
                      isSyncInProgress={isSyncInProgress}
                      onCopyName={onCopyName}
                    />
                  </TreeItemWrapper>
                );
              })}
              {useLazyLoad && pageInfo.hasNextPage && (
                <LoadMoreButton
                  depth={depth + 1}
                  isLoading={isLoadingMoreChildren}
                  loadedCount={childrenToRender.length}
                  totalCount={folderCount}
                  onClick={handleLoadMore}
                />
              )}
            </>
          ) : (
            <TreeEmpty depth={depth + 1} />
          )}
        </div>
      )}
    </div>
  );
}
 